import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import "assets/styles/table.scss";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useState, useRef, useEffect } from "react";
import { Toast } from "primereact/toast";
import { setPage } from "store/productSlice";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import useProduct, { useDeleteAllVendorProducts } from "hooks/useProducts";
import Product from "./Product/Product";
import { useGetVendorsDropdown } from "hooks/useVendors";
import useCategories from "hooks/useCateogries";
import Switch from "@mui/material/Switch";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import CopyProducts from "./CopyProducts/CopyProducts";
import Select from "components/Ui/SelectField/SelectField";
import CopyGroups from "./CopyProducts/CopyGroups";
import AddExcel from "./CopyProducts/AddExcel";
import CategoryOrderModal from "./CategoryOrderModal/CategoryOrderModal";

const Products = () => {
    const { vendors } = useGetVendorsDropdown();
    const { categories } = useCategories();
    const [showCopyGroups, setShowCopyGroups] = useState(false);
    const [showAddExcel, setShowAddExcel] = useState(false);
    const [showCategoryOrder, setShowCategoryOrder] = useState(false);
    const adminInfo = useSelector((state) => state.auth.userData);

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [copyMode, setCopyMode] = useState("all");
    const [showDelete, setShowDelete] = useState(false);
    const [showDeleteAll, setShowDeleteAll] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const { loading, fetchProducts, deleteProduct } = useProduct();
    // Show the full-page spinner only on the first load. On later refetches
    // (search/filter/pagination) keep the list — and the search box — mounted
    // so typing doesn't unmount and refocus the input.
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!loading) setHasLoaded(true);
    }, [loading]);
    const deleteAllMutation = useDeleteAllVendorProducts();
    const page = useSelector((state) => state.product.page);
    const data = useSelector((state) => state.product.products);
    const pages = useSelector((state) => state.product.pages);
    const currentPage = useSelector((state) => state.product.page);
    const dispatch = useDispatch();
    const toast = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        const newParams = {};
        newParams.page = page;
        newParams.size = 12;

        if (searchParams.get("search")) {
            newParams.search = searchParams.get("search");
        }
        if (searchParams.get("vendor")) {
            newParams.vendorId = searchParams.get("vendor");
        }
        if (searchParams.get("category")) {
            newParams.categoryId = searchParams.get("category");
        }
        if (searchParams.get("recent")) {
            newParams.recent = searchParams.get("recent");
        }
        if (searchParams.get("bestseller")) {
            newParams.bestSeller = searchParams.get("bestseller");
        }
        if (searchParams.get("featured")) {
            newParams.featured = searchParams.get("featured");
        }
        fetchProducts(newParams);
    }, [page, searchParams]);

    const handleChangePage = (event, newPage) => {
        dispatch(setPage(newPage));
    };

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    const handleSearch = (value) => {
        if (value) {
            searchParams.set("search", value);
        } else {
            searchParams.delete("search");
        }
        setSearchParams(searchParams);
        dispatch(setPage(1));
    };

    const toggleAddForm = () => {
        navigate("add");
    };

    const toggleCopyForm = () => {
        setShowAddForm((pre) => (pre = !pre));
    };
    const openCopyAll = () => {
        setCopyMode("all");
        setShowAddForm(true);
    };
    const openCopySelected = () => {
        setCopyMode("selected");
        setShowAddForm(true);
    };
    const toggleDelete = () => {
        setShowDelete((pre) => (pre = !pre));
    };

    const toggleDeleteAll = () => {
        setShowDeleteAll((pre) => (pre = !pre));
    };

    const confirmDeleteAll = async () => {
        const vendorId = searchParams.get("vendor");
        if (!vendorId) return;
        try {
            await deleteAllMutation.mutateAsync(vendorId);
            showMessage("warn", "تم الحذف", "تم حذف جميع المنتجات بنجاح");
            toggleDeleteAll();
            fetchProducts({ page, size: 12, vendorId });
        } catch (err) {
            console.error("Error deleting all vendor products:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    const setSelectedProductValue = (product) => {
        setSelectedProduct(product);
    };

    const confirmDelete = async () => {
        try {
            const response = await deleteProduct(selectedProduct.id);
            // Assuming `response` contains information to check if the operation succeeded

            if (response) {
                showMessage("warn", "تم الحذف", "تم حذف المنتج بنجاح");
                toggleDelete();
            } else {
                showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
            }
        } catch (err) {
            console.error("Error deleting product:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    const vendorOptions = vendors?.map((v) => ({
        value: v.id,
        label: v.name,
    }));

    // The restaurant whose menu «ترتيب الأقسام» arranges: the owner's own, or
    // the one an admin chose in the filter. A user id, as in `products.vendorId`.
    const menuVendorId = adminInfo.role === "vendor" ? adminInfo.id : searchParams.get("vendor");
    const menuVendorName =
        adminInfo.role === "vendor"
            ? adminInfo.name
            : vendors?.find((v) => String(v.id) === String(menuVendorId))?.name;

    const categoriesOptions = categories?.map((v) => ({
        value: v.id,
        label: v.name,
    }));
    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            <div style={{ marginTop: "20px", display: "flex", flexWrap: "wrap", gap: "10px" }}>
                <button
                    className="button"
                    onClick={() => {
                        if (
                            data?.length > 0 &&
                            data.every((v) => selectedProducts.includes(v.id))
                        ) {
                            setSelectedProducts((prev) => {
                                const pageIds = data.map((v) => v.id);
                                return prev.filter((id) => !pageIds.includes(id));
                            });
                        } else {
                            setSelectedProducts((prev) => {
                                const pageIds = data.map((v) => v.id);
                                return Array.from(new Set([...prev, ...pageIds]));
                            });
                        }
                    }}
                    style={{ backgroundColor: "gray" }}
                >
                    {data?.length > 0 && data.every((v) => selectedProducts.includes(v.id))
                        ? "إلغاء تحديد الصفحه"
                        : "تحديد الصفحه الحاليه"}
                </button>
                <button className="button" onClick={toggleAddForm}>
                    إضافه منتج
                </button>

                {adminInfo.role !== "vendor" ? (
                    <button className="button" onClick={() => setShowAddExcel(true)}>
                        إضافه اكسيل
                    </button>
                ) : null}

                <button
                    className="button"
                    onClick={() => setShowCopyGroups(true)}
                    style={{ opacity: selectedProducts.length === 0 ? 0.6 : 1 }}
                    disabled={selectedProducts.length === 0}
                >
                    نسخ الاضافات
                </button>
                {menuVendorId ? (
                    <button
                        className="button"
                        title="رتّب أقسام قائمة هذا المطعم كما يراها الزبون"
                        onClick={() => setShowCategoryOrder(true)}
                    >
                        ترتيب الأقسام
                    </button>
                ) : null}
                {searchParams.get("vendor") ? (
                    <button
                        className="button"
                        style={{ backgroundColor: BRAND }}
                        onClick={openCopyAll}
                    >
                        نسخ جميع المنتجات
                    </button>
                ) : null}
                {searchParams.get("vendor") ? (
                    <button
                        className="button"
                        style={{
                            backgroundColor: BRAND,
                            opacity: selectedProducts.length === 0 ? 0.6 : 1,
                        }}
                        onClick={openCopySelected}
                        disabled={selectedProducts.length === 0}
                    >
                        نسخ المنتجات المحدده ({selectedProducts.length})
                    </button>
                ) : null}
                {searchParams.get("vendor") ? (
                    <button
                        className="button"
                        style={{ backgroundColor: "red" }}
                        onClick={toggleDeleteAll}
                    >
                        حذف جميع المنتجات
                    </button>
                ) : null}
            </div>
            {loading && !hasLoaded ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    {adminInfo.role !== "vendor" ? (
                        <div
                            className="row mx-0 row-cols-2 row-cols-md-3 filters"
                            style={{ padding: "20px", alignItems: "center" }}
                        >
                            <div className="col">
                                <h6>المطاعم</h6>
                                <Select
                                    options={[{ value: "", label: "الكل" }, ...vendorOptions]}
                                    placeholder="اختر المطعم"
                                    value={
                                        vendorOptions.find(
                                            (v) => v.value === +searchParams.get("vendor"),
                                        ) || null
                                    }
                                    isSearchable
                                    onChange={(selected) => {
                                        if (!selected || selected.value === "") {
                                            searchParams.delete("vendor");
                                        } else {
                                            searchParams.set("vendor", selected.value);
                                        }
                                        setSearchParams(searchParams);
                                    }}
                                />
                            </div>
                            <div className="col">
                                <h6>الاقسام</h6>
                                <Select
                                    options={[{ value: "", label: "الكل" }, ...categoriesOptions]}
                                    placeholder="اختر الفئه"
                                    value={
                                        categoriesOptions.find(
                                            (v) => v.value === +searchParams.get("category"),
                                        ) || null
                                    }
                                    isSearchable
                                    onChange={(selected) => {
                                        if (!selected || selected.value === "") {
                                            searchParams.delete("category");
                                        } else {
                                            searchParams.set("category", selected.value);
                                        }
                                        setSearchParams(searchParams);
                                    }}
                                />
                            </div>
                            <div className="col">
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                defaultChecked={searchParams.get("recent") || false}
                                                color="warning"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        searchParams.set("recent", true);
                                                        setSearchParams(searchParams);
                                                    }
                                                    if (!e.target.checked) {
                                                        searchParams.delete("recent");
                                                        setSearchParams(searchParams);
                                                    }
                                                }}
                                            />
                                        }
                                        label="ترتيب من الاحدث للاقدم"
                                    />
                                </FormGroup>
                            </div>
                            <div className="col">
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="warning"
                                                defaultChecked={
                                                    searchParams.get("bestseller") || false
                                                }
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        searchParams.set("bestseller", true);
                                                        setSearchParams(searchParams);
                                                    }
                                                    if (!e.target.checked) {
                                                        searchParams.delete("bestseller");
                                                        setSearchParams(searchParams);
                                                    }
                                                }}
                                            />
                                        }
                                        label="ترتيب من الاعلي مبيعا"
                                    />
                                </FormGroup>
                            </div>
                            <div className="col">
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                defaultChecked={
                                                    searchParams.get("featured") || false
                                                }
                                                color="warning"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        searchParams.set("featured", true);
                                                        setSearchParams(searchParams);
                                                    }
                                                    if (!e.target.checked) {
                                                        searchParams.delete("featured");
                                                        setSearchParams(searchParams);
                                                    }
                                                }}
                                            />
                                        }
                                        label="المنتجات المميزه"
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    ) : null}

                    <div className="controls">
                        <SearchInput
                            initialValue={searchParams.get("search") || ""}
                            onSearch={handleSearch}
                            placeholder="بحث بالاسم، رقم المنتج أو الباركود"
                            minWidth={340}
                        />
                    </div>
                    <div
                        className="row mx-0 row-cols-1 row-cols-sm-2  row-cols-md-3 row-cols-lg-4"
                        style={{ padding: "10px" }}
                    >
                        {data.length ? (
                            data.map((product) => (
                                <Product
                                    product={product}
                                    toggleDelete={toggleDelete}
                                    setSelectedProductValue={setSelectedProductValue}
                                    key={product.id}
                                    selectedProducts={selectedProducts}
                                    setSelectedProducts={setSelectedProducts}
                                />
                            ))
                        ) : (
                            <h3>لا يوجد منتجات</h3>
                        )}
                    </div>
                    <Stack spacing={1}>
                        <Pagination
                            count={pages}
                            defaultPage={currentPage}
                            shape="rounded"
                            color={BRAND}
                            style={{ margin: "20px auto 0" }}
                            onChange={handleChangePage}
                        />
                    </Stack>
                    {/* <AddProduct
            show={showAddForm}
            close={toggleAddForm}
            showMessage={showMessage}
          />
          <EditProduct
            show={showEditForm}
            close={toggleEditForm}
            showMessage={showMessage}
            product={selectedProduct}
          /> */}
                    <CopyProducts
                        show={showAddForm}
                        close={toggleCopyForm}
                        showMessage={showMessage}
                        targetVendor={searchParams.get("vendor")}
                        productIds={copyMode === "selected" ? selectedProducts : null}
                        copyMode={copyMode}
                    />
                    <CategoryOrderModal
                        show={showCategoryOrder}
                        close={() => setShowCategoryOrder(false)}
                        vendorUserId={menuVendorId}
                        vendorName={menuVendorName}
                        showMessage={showMessage}
                    />
                    <AddExcel
                        show={showAddExcel}
                        close={() => setShowAddExcel(false)}
                        showMessage={showMessage}
                    />
                    <DeleteItem
                        show={showDelete}
                        close={toggleDelete}
                        loading={loading}
                        confirm={confirmDelete}
                    />
                    <DeleteItem
                        show={showDeleteAll}
                        close={toggleDeleteAll}
                        loading={loading || deleteAllMutation.isPending}
                        confirm={confirmDeleteAll}
                    />
                    <CopyGroups
                        show={showCopyGroups}
                        close={() => setShowCopyGroups(false)}
                        showMessage={showMessage}
                        selectedProducts={selectedProducts}
                    />
                </>
            )}
        </div>
    );
};
export default Products;
